/**
 * Which profile THIS device shows (ADR-036, plan panel). The id is persisted on the first successful resolve, so a
 * device that started in a one-profile realm cannot be orphaned the moment a second profile appears — and a profile
 * that later disappears clears itself instead of stranding a screen nobody can reach.
 *
 * `localStorage` is per-device by definition, which is exactly the scope wanted here; every access is guarded because
 * a locked-down browser can throw on it.
 */
const KEY = "arc_kiosk_profile";

export function readDeviceProfileId(): number | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return null;
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export function rememberDeviceProfileId(id: number): void {
  try {
    window.localStorage.setItem(KEY, String(id));
  } catch {
    /* a device with storage disabled simply re-resolves each visit */
  }
}

export function forgetDeviceProfileId(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Strip `?arc_kiosk` from the address bar once it has been consumed (diff panel, Medium). The link is a ONE-TIME
 * pointer — "open it once on the device; it remembers the profile from then on". Left in place it outranks the stored
 * id on EVERY reload, so a device that was later re-pointed (its first profile deleted, an operator picking the
 * replacement) would silently revert to the dead id and fall back to the picker every time it restarts.
 */
export function consumeKioskParam(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("arc_kiosk")) return;
    url.searchParams.delete("arc_kiosk");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* a browser refusing history writes keeps the param — the stored id still wins on the visit after that */
  }
}

/** `?arc_kiosk=<id>` — how an admin points a device at a profile the first time. */
export function readKioskParam(search: string): number | null {
  const raw = new URLSearchParams(search).get("arc_kiosk");
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 && id <= 2147483647 ? id : null;
}

/**
 * The t("viewer.leaveKiosk") escape hatch (ADR-036 D3 guard 2). Because the kiosk suppresses all chrome, a
 * mis-configured account must be able to get out IN-PRODUCT rather than needing someone with Keycloak access.
 *
 * `sessionStorage`, deliberately: leaving survives a reload (an admin looking around does not get thrown back by a
 * stray refresh) but not a new browser session, so a device left alone returns to kiosk mode on its own. "Return to
 * kiosk view" clears it immediately, so no one has to clear storage by hand either.
 */
const SUSPEND_KEY = "arc_kiosk_suspended";

export function isKioskSuspended(): boolean {
  try {
    return window.sessionStorage.getItem(SUSPEND_KEY) === "1";
  } catch {
    return false; // a storage-denied browser simply cannot remember the escape — kiosk mode still renders
  }
}

export function suspendKiosk(): void {
  try {
    window.sessionStorage.setItem(SUSPEND_KEY, "1");
  } catch {
    /* the in-memory flag the caller holds still applies for this render */
  }
}

export function resumeKiosk(): void {
  try {
    window.sessionStorage.removeItem(SUSPEND_KEY);
  } catch {
    /* nothing to clear */
  }
}
